import React, { useEffect, useState } from 'react';
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useBookingFormValidation } from "./useBookingFormValidation";
import { submitBooking } from "../Requests/SubmitBooking";
import {
    bookingFormCategoryLoaded,
    bookingFormDateChanged,
    bookingFormValueChanged,
    bookingFormValuesChanged,
    bookingTimeSelectedForAmenity,
    resetBookingFlow,
    setTagFilter
} from "../Redux/BookingFormSlice";
import {
    dateForStateFromDateInput,
    dateFromState,
    setDateKeepTime
} from "../../../Helpers/DateTimeHelper";
import { isSameDay } from "date-fns";
import { canChangeOtherUsers as canChangeOtherUsersFunc } from "../Helpers/PermissionHelper";
import { bookingConfigLoadedForForm } from "../Redux/BookingGlobalActions";
import {
    getCategoriesData,
    getCategoryAndAmenitiesData,
    getAmenityData,
    getEditBookingData, getRestoreBookingData
} from "../Requests/BookingFormRequests";
import { URL } from "../Bookings";
import { MODAL_EDIT_BOOKING } from "../Pages/BookingsLog";
import { hideModal } from "../../../Redux/GlobalReducer";

export const BOOKING_FLOW_STATE = {
    SELECT_CATEGORY: 'select-category',
    SELECT_AMENITY: 'select-amenity',
    BOOKING_FORM: 'booking-form',
};

export const BookingFormContext = React.createContext({
    bookingFlowState: null,

    currentUser: null,
    canChangeOtherUsers: null,
    hasMultipleUnits: null,
    amenityViewMode: null,

    categories: null,

    date: null,
    category: null,
    amenities: null,
    amenity: null,
    booking: null,
    errors: null,

    bookingConfig: null,

    tags: null,
    allTagOptions: null,
    onSetTagFilter: () => {},

    onBookingFormChange: () => {},
    onBookingFormChangeMultiple: () => {},
    onBookingFormChangeEvent: () => {},

    onBookingDateChange: () => {},
    onBookingDateInputChangeEvent: () => {},
    onBookingIntervalChange: () => {},
    setErrorInTimeline: () => {},

    onSelectCategory: () => {},
    onSelectAmenityAndBookingTime: () => {},

    bookingInProgress: false,
    isSubmitDisabled: false,
    onSubmitClick: () => {},

    editBookingId: null,
});

const BookingFormProvider = (props) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {categorySlug, amenityViewMode, amenitySlug} = useParams();

    const currentUser = useSelector(state => state.global.currentUser);
    const permissions = useSelector(state => state.module.BOOKINGS.common.permissions);
    const canChangeOtherUsers = canChangeOtherUsersFunc(permissions, false)
    const hasMultipleUnits = currentUser?.units.length > 1;

    const savedAmenityViewMode = useSelector(state => state.module.BOOKINGS.bookingForm.savedAmenityViewMode);

    const categories = useSelector(state => state.module.BOOKINGS.bookingForm.categories);

    const bookingDate = useSelector(state => state.module.BOOKINGS.bookingForm.date);
    const category = useSelector(state => state.module.BOOKINGS.bookingForm.category);
    const amenities = useSelector(state => state.module.BOOKINGS.bookingForm.amenities);

    const tags = useSelector(state => state.module.BOOKINGS.bookingForm.tags);
    const allTagOptions = useSelector(state => state.module.BOOKINGS.common.formOptions.tags);

    const amenity = useSelector(state=> state.module.BOOKINGS.bookingForm.amenity);
    const bookingConfig = useSelector(state => state.module.BOOKINGS.bookingForm.bookingConfig);

    const booking = useSelector(state => state.module.BOOKINGS.bookingForm.booking);
    const serverErrors = useSelector(state => state.module.BOOKINGS.bookingForm.errors);

    const confirmedBookingId = useSelector(state => state.module.BOOKINGS.bookingForm.confirmedBookingId);

    const editBookingId = props.editBookingId ?? null;
    const restoreBookingId = useSelector(state => state.module.BOOKINGS.bookingForm.restoreBookingId);

    const {submitValidatedForm, validate, errors} = useBookingFormValidation(
        bookingConfig, category, amenity, serverErrors,
        () => {
            dispatch( submitBooking(bookingConfig, category, amenity, booking) )
        },
    );

    const [bookingFlowState, setBookingFlowState] = useState(null);

    const [errorInTimeline, setErrorInTimeline] = React.useState(false);
    const [bookingInProgress, setBookingInProgress] = useState(false);

    /**
     * Figure out what state in the flow we're at based on the available data
     * and set a single state variable
     */
    useEffect(() => {
        let newState = null;
        if (!!restoreBookingId) {
            newState = BOOKING_FLOW_STATE.BOOKING_FORM;
        } else if (!!editBookingId) {
            newState = BOOKING_FLOW_STATE.BOOKING_FORM;
        } else if (!categorySlug && !amenitySlug) {
            newState = BOOKING_FLOW_STATE.SELECT_CATEGORY;
        } else if (!!categorySlug && !amenitySlug) {
            newState = BOOKING_FLOW_STATE.SELECT_AMENITY;
        } else if (!!categorySlug && !!amenitySlug) {
            newState = BOOKING_FLOW_STATE.BOOKING_FORM;
        }

        if (newState === BOOKING_FLOW_STATE.SELECT_CATEGORY) {
            navigate(URL.MAKE_A_BOOKING);
        }

        if (newState !== bookingFlowState) {
            setBookingFlowState(newState);
            initialiseThisPage(newState);
        }

    }, [categorySlug, amenitySlug, booking?.user_id, booking?.unit_id]);


    /**
     * Conditions to refresh the data for the Select Category State
     */
    useEffect(() => {
        if (bookingFlowState !== BOOKING_FLOW_STATE.SELECT_CATEGORY) {
            return;
        }

        updateThisPageData();
    }, [bookingFlowState, booking?.unit_id]);


    /**
     * Conditions to refresh the data for the Select Amenity state
     */
    useEffect(() => {
        if (bookingFlowState !== BOOKING_FLOW_STATE.SELECT_AMENITY) {
            return;
        }

        updateThisPageData(bookingDate);
    }, [bookingFlowState, categorySlug, tags, currentUser, booking?.unit_id])


    /*
     * Conditions to refresh the data for the booking form state
     */
    useEffect(() => {
        if (bookingFlowState !== BOOKING_FLOW_STATE.BOOKING_FORM) {
            return;
        }

        // We don't need to update the data if we're editing as
        // that will be retrieved elsewhere from the edit booking request
        if (!!editBookingId) {
            return;
        }

        updateThisPageData(bookingDate, bookingConfig.boundary);
    }, [bookingFlowState]);

    const maybeInitialiseBookingUserAndUnit = () => {
        let newUserId = booking?.user_id;
        let newUnitId = booking?.unit_id;
        if (!canChangeOtherUsersFunc(permissions)) {
            newUserId = currentUser.id;
        }

        if (!hasMultipleUnits) {
            newUnitId = currentUser.units[0].id;
        }

        handleBookingFormChangeMultiple({
            user_id: newUserId,
            unit_id: newUnitId,
        })
    }

    /**
     * Perform any initialisation required when entering each booking flow state
     */
    const initialiseThisPage = (forBookingFlowState) => {
        switch (forBookingFlowState) {
            case BOOKING_FLOW_STATE.SELECT_CATEGORY:
                window.scrollTo(0,0);
                maybeInitialiseBookingUserAndUnit();
                break;

            case BOOKING_FLOW_STATE.SELECT_AMENITY:
                window.scrollTo(0,0);
                maybeInitialiseBookingUserAndUnit();
                break;

            case BOOKING_FLOW_STATE.BOOKING_FORM:
                window.scrollTo(0,0);
                maybeInitialiseBookingUserAndUnit();
                break;
        }
    }

    /**
     * Get the category slug to use in the request, if we are editing it will be from
     * the category in the booking form state, otherwise it will be from the URL parameter
     * @returns {string}
     */
    function getCategorySlug() {
        return amenitySlug ? categorySlug : category.slug
    }

    /**
     * Get the amenity slug to use in the request, if we are editing it will be from
     * the amenity in the booking form state, otherwise it will be from the URL parameter
     * @returns {string}
     */
    function getAmenitySlug() {
        return amenitySlug ?? amenity.slug;
    }

    const updateThisPageData = (updateDate, updateBoundary) => {
        switch (bookingFlowState) {
            case BOOKING_FLOW_STATE.SELECT_CATEGORY:
                dispatch(
                    getCategoriesData(booking.unit_id)
                );
                break;

            case BOOKING_FLOW_STATE.SELECT_AMENITY:
                dispatch(
                    getCategoryAndAmenitiesData(categorySlug, booking?.unit_id, updateDate, tags)
                );
                break;

            case BOOKING_FLOW_STATE.BOOKING_FORM:
                const updateCategorySlug = getCategorySlug();
                const updateAmenitySlug = getAmenitySlug();

                // Prevent situation where it might attempt to refresh amenity data
                // after clicking back button on payment screen.
                if (updateCategorySlug && updateAmenitySlug) {
                    dispatch(
                        getAmenityData(
                            updateCategorySlug,
                            updateAmenitySlug,
                            updateDate,
                            null,
                            booking.unit_id,
                            booking.id
                        )
                    );
                }
                break;
        }
    }

    /**
     * Revalidate booking every time booking state changes, if we have errors already
     * */
    useEffect(() => {
        if (Object.keys(errors).length) {
            validate(booking).then(() => true);
        }
    }, [booking])

    /**
     * Triggers the url change to show the confirmation after successful submission
     */
    useEffect(() => {
        if (!confirmedBookingId) {
            return;
        }

        if (editBookingId) {
            dispatch( hideModal( MODAL_EDIT_BOOKING ) );
            dispatch( resetBookingFlow() );
        } else {
            navigate(`/bookings/confirmation/${confirmedBookingId}`);
        }

    }, [confirmedBookingId]);

    useEffect(() => {
        setBookingInProgress(false)
    }, [errors]);

    /**
     * Load the booking data to edit if we've set an editBookingId
     */
    useEffect(() => {
        if (editBookingId) {
            const loadEditData = async () => {
                dispatch(getEditBookingData(editBookingId));
            }
            loadEditData();
        }
    }, [editBookingId]);

    useEffect(() => {
        if (restoreBookingId) {
            const loadRestoreData = async () => {
                await dispatch(getRestoreBookingData(restoreBookingId, navigate));
            }
            loadRestoreData();
        }
    }, [restoreBookingId]);


    const handleBookingFormChange = (name, value) => {
        dispatch( bookingFormValueChanged(name, value) );
    }

    // Update multiple values at once
    const handleBookingFormChangeMultiple = (values) => {
        dispatch( bookingFormValuesChanged(values) );
    }

    const handleBookingFormChangeEvent = (evt) => {
        handleBookingFormChange(evt.target.name, evt.target.value);
    }

    const handleBookingIntervalChange = (interval) => {
        handleBookingFormChangeMultiple({
            start: interval?.start ?? null,
            end: interval?.end ?? null,
            // Also clear any existing recurrence information when changing date
            recurrence: {},
            rescheduled_occurrences: {}
        })

        if (interval?.start
            && !isSameDay(dateFromState(interval.start), dateFromState(bookingDate))
        ) {
            const newDate = setDateKeepTime(bookingDate, interval.start);
            handleBookingDateChange(newDate);
        }
    }

    const handleBookingDateChange = (newDate) => {
        dispatch( bookingFormDateChanged(newDate) )

        // We don't need to trigger a refresh if the only the time of day has changed
        // We might just have clicked on the timeline at a different time, or moved the calendar time
        if (!isSameDay(dateFromState(newDate), dateFromState(bookingDate))) {
            console.log('Update DATE CHANGE')
            updateThisPageData(newDate);
        }
    }

    const handleBookingDateInputChangeEvent = (event) => {
        const newDate = (booking.date)
            ? setDateKeepTime(
                booking.date,
                event.target.value
            )
            : event.target.value
        ;

        handleBookingDateChange(dateForStateFromDateInput(newDate));
    }

    const handleSelectCategory = (categoryKey) => {
        const clickedCategory = categories[categoryKey];
        dispatch( bookingFormCategoryLoaded(clickedCategory, null) );
        const mode = amenityViewMode ?? savedAmenityViewMode;
        navigate(`/bookings/book/${clickedCategory.slug}/${mode}`);
    }

    /**
     * This is the action which is triggered when the user selects a date on an amenity.
     * It copies the currently selected date (& unit) values to the booking form redux state slice.
     * @param amenityId
     * @param selectedInterval
     */
    const handleSelectAmenityAndBookingTime = (amenityId, selectedInterval) => {
        if (bookingFlowState !== BOOKING_FLOW_STATE.SELECT_AMENITY) {
            return null;
        }

        dispatch(
            bookingTimeSelectedForAmenity(selectedInterval.start, selectedInterval.end)
        );

        const {amenity, config} = amenities[amenityId] ?? null;
        const mode = amenityViewMode ?? savedAmenityViewMode;

        if (!amenity) {
            alert("Unknown amenity selected");
            return;
        }

        // Move data for selected amenity into the booking form state
        dispatch( bookingConfigLoadedForForm(amenity, category, config) );

        setTimeout(() => {
            navigate(`/bookings/book/${categorySlug}/${mode}/${amenity.slug}`);
        }, 100);
    }

    const handleSubmitClick = async () => {
        let bookingToSubmit = {...booking};

        if (!bookingToSubmit.visitors_added) {
            delete bookingToSubmit.visitors;
        }

        setBookingInProgress(true);
        await submitValidatedForm(bookingToSubmit);
        setBookingInProgress(false);
    }

    const isSubmitDisabled = () => {
        if (bookingInProgress) {
            return false;
        }
        return (errorInTimeline || (Object.keys(errors).length > 0))
    }

    const values = {
        bookingFlowState,

        currentUser,
        permissions,
        canChangeOtherUsers,
        hasMultipleUnits,
        amenityViewMode: amenityViewMode ?? savedAmenityViewMode,

        categories,

        date: bookingDate,
        category,
        amenities,
        amenity,
        booking,
        errors,

        bookingConfig,

        tags,
        allTagOptions,

        onSetTagFilter: (value) => dispatch( setTagFilter(value) ),

        onBookingDateChange: (value) => handleBookingDateChange(value),
        onBookingDateInputChangeEvent: (event) => handleBookingDateInputChangeEvent(event),

        onBookingFormChange: (name, value) => handleBookingFormChange(name, value),
        onBookingFormChangeMultiple: (values) => handleBookingFormChangeMultiple(values),
        onBookingFormChangeEvent: (event) => handleBookingFormChangeEvent(event),
        onBookingIntervalChange: (value) => handleBookingIntervalChange(value),

        setErrorInTimeline: (value) => setErrorInTimeline(value),

        onSelectCategory: handleSelectCategory,
        onSelectAmenityAndBookingTime: handleSelectAmenityAndBookingTime,

        bookingInProgress,
        isSubmitDisabled,
        onSubmitClick: (event) => handleSubmitClick(event),

        editBookingId,
    }

    return (
        <BookingFormContext.Provider
            value={{...values}}
        >
            {(restoreBookingId)
                ? <div>Restoring your previous form values...</div>
                : props.children
            }
        </BookingFormContext.Provider>);
};

BookingFormProvider.propTypes = {
    editBookingId: PropTypes.string,
};

BookingFormProvider.defaultProps = {};

export default BookingFormProvider;
